/**
 * useConnectivity.ts — app-wide backend reachability (the Connectivity
 * button + the start page's body-hiding gate).
 *
 * `ConnectivityProvider` mounts ONE health poll for the whole app: it probes
 * `GET {origin}/health` every 5s (plus on window focus) against the connected
 * backend's origin — or the conventional default (`http://localhost:8000`)
 * before any connection is configured. The VSKI storage detail is NOT polled
 * here: the wizard owns its own `/api/vski/status` probes while open, and the
 * button state (backend + WS connection) doesn't depend on storage.
 *
 * `useBackendHealth` is the wizard's own probe against its DRAFT origin (the
 * user may point at a custom address before committing the connection) — it
 * re-probes on an interval so the Next button lights up the moment the stack
 * finishes `docker compose up`.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from '../auth/AuthContext';
import { deriveConnectivity, type Connectivity, type ConnectivitySnapshot } from './connectivity';
import { DEFAULT_HTTP_ORIGIN, wsUrlToOrigin } from './httpOrigin';

const POLL_MS = 3000;

interface ConnectivityState {
  /** True until the first health probe lands — guards the start page against
   *  flashing the wizard over a healthy backend on boot. */
  checking: boolean;
  snapshot: ConnectivitySnapshot;
  derived: Connectivity;
  /** Force an immediate re-probe (e.g. after the wizard commits a connection). */
  reload: () => void;
}

const ConnectivityContext = createContext<ConnectivityState | null>(null);

export function ConnectivityProvider({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();
  const origin = useMemo(
    () => wsUrlToOrigin(auth.wsUrl ?? '') ?? DEFAULT_HTTP_ORIGIN,
    [auth.wsUrl],
  );
  const [backendOk, setBackendOk] = useState<boolean | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const probe = (): void => {
      fetch(`${origin}/health`, { mode: 'no-cors' })
        .then(() => {
          if (!cancelled) setBackendOk(true);
        })
        .catch(() => {
          if (!cancelled) setBackendOk(false);
        });
    };
    probe();
    const timer = window.setInterval(probe, POLL_MS);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') probe();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [origin, nonce]);

  const value = useMemo<ConnectivityState>(() => {
    const snapshot: ConnectivitySnapshot = {
      wsUrl: auth.wsUrl,
      backendOk,
      // Storage detail is the wizard's concern (it runs its own VSKI probes);
      // `connected` never depends on it.
      vski: null,
    };
    return {
      checking: backendOk === null,
      snapshot,
      derived: deriveConnectivity(snapshot),
      reload: () => setNonce((n) => n + 1),
    };
  }, [auth.wsUrl, backendOk]);

  return <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>;
}

export function useConnectivity(): ConnectivityState {
  const ctx = useContext(ConnectivityContext);
  if (!ctx) throw new Error('useConnectivity must be used within <ConnectivityProvider>.');
  return ctx;
}

/** Health probe against a specific origin (the wizard's draft backend).
 * `ok` is null while the first probe is in flight, false on failure or an
 * invalid origin. Polls while mounted so the wizard reacts to the stack
 * coming up without a manual recheck. */
export function useBackendHealth(origin: string | null): {
  ok: boolean | null;
  recheck: () => void;
} {
  const [ok, setOk] = useState<boolean | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!origin) {
      setOk(false);
      return undefined;
    }
    let cancelled = false;
    const probe = (): void => {
      fetch(origin, { mode: 'no-cors' })
        .then(() => {
          if (!cancelled) setOk(true);
        })
        .catch(() => {
          if (!cancelled) setOk(false);
        });
    };
    probe();
    const timer = window.setInterval(probe, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [origin, nonce]);

  return { ok, recheck: () => setNonce((n) => n + 1) };
}
